import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { TypeGuard, type Timezone } from '@principle-theorem/shared';
import { isBoolean, isNumber, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';

export const PATIENT_MEDIA_RESOURCE_TYPE = 'patientMedia';

export const PATIENT_MEDIA_SOURCE_ENTITY: ISourceEntity = SourceEntity.init({
  metadata: {
    label: 'Patient Media List',
    description: '',
    idPrefix: PATIENT_MEDIA_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export enum CorePracticeMediaCategory {
  CephalometricXRay = 1,
  IntraoralXRay = 2,
  IntraoralPhoto = 3,
  PanoramicXRay = 4,
  DigitalPhoto = 5,
  Documents = 6,
}

export const CORE_PRACTICE_MEDIA_CATEGORY_TAG_NAME_MAP: Record<
  CorePracticeMediaCategory,
  string
> = {
  [CorePracticeMediaCategory.CephalometricXRay]: 'Cephalometric X-Ray',
  [CorePracticeMediaCategory.IntraoralXRay]: 'Intraoral X-Ray',
  [CorePracticeMediaCategory.IntraoralPhoto]: 'Intraoral Photo',
  [CorePracticeMediaCategory.PanoramicXRay]: 'Panoramic X-Ray',
  [CorePracticeMediaCategory.DigitalPhoto]: 'Digital Photo',
  [CorePracticeMediaCategory.Documents]: 'Documents',
};

export interface ICorePracticePatientMedia {
  id: string; // 3q_tCnL6AkK7Ylnkdctimg
  fileName: string; // "SLmB34dpA0CWE7hc7SLnVw.png"
  dateTaken: string; // 2024-03-02 14:27:39.890
  patientId: number;
  categoryId?: CorePracticeMediaCategory;
  isDeleted: boolean;
}

export function isCorePracticeMedia(
  item: unknown,
  logError: boolean
): item is ICorePracticePatientMedia {
  return TypeGuard.interface<ICorePracticePatientMedia>(
    {
      id: isString,
      fileName: isString,
      dateTaken: isString,
      patientId: isNumber,
      categoryId: TypeGuard.nilOr(
        TypeGuard.enumValue(CorePracticeMediaCategory)
      ),
      isDeleted: isBoolean,
    },
    logError
  )(item);
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ICorePracticePatientMediaTranslations {}

export interface ICorePracticePatientMediaFilters {
  patientId: number;
}

const PATIENT_MEDIA_SOURCE_QUERY = `
SELECT
  Identifier AS id,
  FileName AS file_name,
  DateTaken AS date_taken,
  PatientId AS patient_id,
  CategoryId AS category_id,
  convert_to_boolean(IsDeleted) AS is_deleted
FROM tblMedia
`;

export type CorePracticePatientMedia = ISourceEntityTypes<
  ICorePracticePatientMedia,
  ICorePracticePatientMediaTranslations,
  ICorePracticePatientMediaFilters
>;

export class PatientMediaSourceEntity extends BaseSourceEntity<CorePracticePatientMedia> {
  sourceEntity = PATIENT_MEDIA_SOURCE_ENTITY;
  entityResourceType = PATIENT_MEDIA_RESOURCE_TYPE;
  sourceQuery = PATIENT_MEDIA_SOURCE_QUERY;
  verifySourceFn = isCorePracticeMedia;

  translate(
    _data: ICorePracticePatientMedia,
    _timezone: Timezone
  ): ICorePracticePatientMediaTranslations {
    return {};
  }

  getSourceRecordId(data: ICorePracticePatientMedia): string {
    return data.id;
  }

  getSourceLabel(data: ICorePracticePatientMedia): string {
    return `${data.id} - ${data.fileName}`;
  }

  getFilterData(
    data: ICorePracticePatientMedia,
    _timezone: Timezone
  ): ICorePracticePatientMediaFilters {
    return {
      patientId: data.patientId,
    };
  }
}
